export interface BootCacheEntry {
  /** The build, tenant and user the value was saved for (see bootCache.ts). */
  scope: string;
  savedAt: number;
  value: unknown;
}

export interface BootCacheStore {
  get(key: string): Promise<BootCacheEntry | undefined>;
  set(key: string, entry: BootCacheEntry): Promise<void>;
  clear(): Promise<void>;
}

const DATABASE = 'fd-boot-cache';
const ENTRIES = 'entries';

function settle<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function indexedDbBootCacheStore(factory: IDBFactory = indexedDB): BootCacheStore {
  let database: Promise<IDBDatabase> | undefined;
  const open = () => {
    if (!database) {
      const request = factory.open(DATABASE, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(ENTRIES);
      database = settle(request);
      // A failed open (private mode, a blocked upgrade) is retried by the next call.
      database.catch(() => {
        database = undefined;
      });
    }
    return database;
  };
  const entries = async (mode: IDBTransactionMode) =>
    (await open()).transaction(ENTRIES, mode).objectStore(ENTRIES);

  return {
    get: async (key) => settle((await entries('readonly')).get(key)),
    set: async (key, entry) => {
      await settle((await entries('readwrite')).put(entry, key));
    },
    clear: async () => {
      await settle((await entries('readwrite')).clear());
    },
  };
}

export function memoryBootCacheStore(): BootCacheStore {
  const entries = new Map<string, BootCacheEntry>();
  return {
    get: async (key) => structuredClone(entries.get(key)),
    set: async (key, entry) => {
      entries.set(key, structuredClone(entry));
    },
    clear: async () => entries.clear(),
  };
}
